/**
 * GainLossIndicator Component
 *
 * Displays gain/loss with color coding, arrows, and percentage
 * Green for gains, red for losses, gray for neutral
 */

import React from 'react';
import { formatCurrency, formatPercent, getGainLossColor, getArrowIcon } from '../utils/priceCalculations';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * GainLossIndicator Component
 *
 * @param {Object} props
 * @param {number} props.gainLoss - Absolute gain/loss amount
 * @param {number} props.gainLossPercent - Percentage gain/loss
 * @param {string} props.currency - Currency code (USD, GBP, EUR)
 * @param {boolean} props.showAmount - Show dollar amount (default: true)
 * @param {boolean} props.showPercent - Show percentage (default: true)
 * @param {boolean} props.showArrow - Show arrow icon (default: true)
 * @param {string} props.size - Size variant ('sm', 'md', 'lg') (default: 'md')
 * @param {string} props.className - Additional CSS classes
 */
export const GainLossIndicator = ({
  gainLoss = 0,
  gainLossPercent = 0,
  currency = 'EUR',
  showAmount = true,
  showPercent = true,
  showArrow = true,
  size = 'md',
  className = ''
}) => {
  // Size variants
  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  // Determine color and icon
  const colorClass = getGainLossColor(gainLoss);
  const Icon = gainLoss > 0 ? TrendingUp : gainLoss < 0 ? TrendingDown : Minus;

  // Format values
  const amountText = formatCurrency(Math.abs(gainLoss), currency, 2);
  const percentText = formatPercent(gainLossPercent, 2);
  const sign = gainLoss >= 0 ? '+' : '-';

  return (
    <div className={`flex items-center gap-1.5 ${sizeClasses[size]} ${colorClass} ${className}`}>
      {showArrow && <Icon className={iconSizes[size]} />}

      <div className="flex items-center gap-1 font-semibold">
        {showAmount && (
          <span>
            {sign}{amountText}
          </span>
        )}

        {showAmount && showPercent && <span>•</span>}

        {showPercent && <span>{percentText}</span>}
      </div>
    </div>
  );
};

/**
 * CompactGainLossIndicator Component
 * Minimal inline gain/loss display (percentage only)
 *
 * @param {Object} props
 * @param {number} props.gainLossPercent - Percentage gain/loss
 * @param {string} props.className - Additional CSS classes
 */
export const CompactGainLossIndicator = ({ gainLossPercent = 0, className = '' }) => {
  const colorClass = getGainLossColor(gainLossPercent);
  const arrow = getArrowIcon(gainLossPercent);

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${colorClass} ${className}`}>
      <span>{arrow}</span>
      <span>{formatPercent(gainLossPercent, 2)}</span>
    </span>
  );
};

/**
 * GainLossBadge Component
 * Pill-style badge for gain/loss
 *
 * @param {Object} props
 * @param {number} props.gainLoss - Absolute gain/loss amount
 * @param {number} props.gainLossPercent - Percentage gain/loss
 * @param {string} props.currency - Currency code
 * @param {string} props.className - Additional CSS classes
 */
export const GainLossBadge = ({
  gainLoss = 0,
  gainLossPercent = 0,
  currency = 'EUR',
  className = ''
}) => {
  const isPositive = gainLoss > 0;
  const isNegative = gainLoss < 0;
  const isNeutral = gainLoss === 0;

  const badgeClasses = isPositive
    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
    : isNegative
    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
    : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';

  const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
  const sign = gainLoss >= 0 ? '+' : '-';

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badgeClasses} ${className}`}
    >
      <Icon className="h-3 w-3" />
      <span>
        {sign}{formatCurrency(Math.abs(gainLoss), currency, 2)}
      </span>
      <span>({formatPercent(gainLossPercent, 2)})</span>
    </span>
  );
};

/**
 * GainLossCard Component
 * Card-style display for gain/loss metrics
 *
 * @param {Object} props
 * @param {string} props.label - Label text (e.g., "Total Gain/Loss")
 * @param {number} props.gainLoss - Absolute gain/loss amount
 * @param {number} props.gainLossPercent - Percentage gain/loss
 * @param {string} props.currency - Currency code
 * @param {string} props.className - Additional CSS classes
 */
export const GainLossCard = ({
  label = 'Gain/Loss',
  gainLoss = 0,
  gainLossPercent = 0,
  currency = 'EUR',
  className = ''
}) => {
  const colorClass = getGainLossColor(gainLoss);
  const Icon = gainLoss > 0 ? TrendingUp : gainLoss < 0 ? TrendingDown : Minus;
  const sign = gainLoss >= 0 ? '+' : '';

  return (
    <div className={`p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</span>
        <Icon className={`h-5 w-5 ${colorClass}`} />
      </div>

      <div className="mt-2 space-y-1">
        <div className={`text-2xl font-bold ${colorClass}`}>
          {sign}{formatCurrency(Math.abs(gainLoss), currency, 2)}
        </div>
        <div className={`text-sm font-semibold ${colorClass}`}>
          {formatPercent(gainLossPercent, 2)}
        </div>
      </div>
    </div>
  );
};

/**
 * InlineGainLoss Component
 * Inline text display for gain/loss (used in tables)
 *
 * @param {Object} props
 * @param {number} props.currentValue - Current market value
 * @param {number} props.costBasis - Original cost basis
 * @param {string} props.currency - Currency code
 * @param {string} props.className - Additional CSS classes
 */
export const InlineGainLoss = ({
  currentValue = 0,
  costBasis = 0,
  currency = 'EUR',
  className = ''
}) => {
  const gainLoss = currentValue - costBasis;
  const gainLossPercent = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0;
  const colorClass = getGainLossColor(gainLoss);
  const sign = gainLoss >= 0 ? '+' : '';

  return (
    <div className={`${colorClass} ${className}`}>
      <div className="font-semibold">
        {sign}{formatCurrency(Math.abs(gainLoss), currency, 2)}
      </div>
      <div className="text-xs">
        ({formatPercent(gainLossPercent, 2)})
      </div>
    </div>
  );
};

export default GainLossIndicator;
